<template>
    <v-container fluid fill-height style="padding: 1rem">
        <v-layout column>
            <v-flex>
                <v-alert border="left" colored-border type="info" elevation="2" class="mb-4">
                    {{ $t('text.hierarchical-job-sorting-description') }}
                </v-alert>

                <v-card v-if="loading" class="pa-4">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    <span class="ml-2">{{ $t('text.loading-jobs') }}</span>
                </v-card>

                <v-card v-else-if="sortedDepartments.length === 0" class="pa-4">
                    <v-icon large color="grey" class="mr-2">work_off</v-icon>
                    <span>{{ $t('text.no-published-jobs') }}</span>
                </v-card>

                <v-card v-else>
                    <v-card-title>
                        <v-icon left>reorder</v-icon>
                        {{ $t('label.hierarchical-job-order') }}
                        <v-spacer></v-spacer>
                        <v-btn v-if="hasChanges" color="primary" @click="handleSave" :loading="saving">
                            <v-icon left>save</v-icon>
                            {{ $t('button.save') }}
                        </v-btn>
                        <v-btn v-if="hasChanges" text @click="handleReset" :disabled="saving">
                            {{ $t('button.reset') }}
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <p class="text-body-2 grey--text mb-4">
                            {{ $t('text.drag-to-reorder-hierarchy') }}
                        </p>

                        <Draggable v-model="sortedDepartments" @start="handleDragStart" @end="handleDragEnd"
                            handle=".department-drag-handle" :group="'departments'">
                            <transition-group name="department-item" tag="div">
                                <v-expansion-panels v-for="(department, deptIndex) in sortedDepartments"
                                    :key="`dept_${department.id}`" class="mb-3 department-item" multiple
                                    :value="expandedDepartments" @input="updateExpandedDepartments">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header>
                                            <v-row no-gutters align="center" class="department-header">
                                                <v-col cols="auto" class="mr-3">
                                                    <div class="department-drag-handle">
                                                        <v-icon color="grey" class="drag-icon">
                                                            drag_indicator
                                                        </v-icon>
                                                    </div>
                                                </v-col>
                                                <v-col cols="auto" class="mr-3">
                                                    <div class="order-number">
                                                        {{ deptIndex + 1 }}
                                                    </div>
                                                </v-col>
                                                <v-col>
                                                    <div class="department-title">
                                                        <v-icon small class="mr-2">business</v-icon>
                                                        {{ department.name }}
                                                    </div>
                                                    <div class="department-meta text-caption grey--text">
                                                        {{ department.teams?.length || 0 }} {{ $t('label.teams') }},
                                                        {{ getTotalJobsInDepartment(department) }} {{ $t('label.jobs')}}
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-expansion-panel-header>

                                        <v-expansion-panel-content>
                                            <Draggable v-model="department.teams" @start="handleDragStart"
                                                @end="handleDragEnd" handle=".team-drag-handle"
                                                :group="`teams-${department.id}`">
                                                <transition-group name="team-item" tag="div">
                                                    <v-expansion-panels v-for="(team, teamIndex) in department.teams"
                                                        :key="`team_${team.id}`" class="mb-2 team-item ml-4" multiple
                                                        :value="expandedTeams" @input="updateExpandedTeams">
                                                        <v-expansion-panel>
                                                            <v-expansion-panel-header>
                                                                <v-row no-gutters align="center" class="team-header">
                                                                    <v-col cols="auto" class="mr-3">
                                                                        <div class="team-drag-handle">
                                                                            <v-icon color="grey" class="drag-icon"
                                                                                small>
                                                                                drag_indicator
                                                                            </v-icon>
                                                                        </div>
                                                                    </v-col>
                                                                    <v-col cols="auto" class="mr-3">
                                                                        <div class="order-number small">
                                                                            {{ teamIndex + 1 }}
                                                                        </div>
                                                                    </v-col>
                                                                    <v-col>
                                                                        <div class="team-title">
                                                                            <v-icon small class="mr-2">group</v-icon>
                                                                            {{ team.name }}
                                                                        </div>
                                                                        <div class="team-meta text-caption grey--text">
                                                                            {{ team.jobs?.length || 0 }} {{$t('label.jobs') }}
                                                                        </div>
                                                                    </v-col>
                                                                </v-row>
                                                            </v-expansion-panel-header>

                                                            <v-expansion-panel-content>
                                                                <Draggable v-model="team.jobs" @start="handleDragStart"
                                                                    @end="handleDragEnd" handle=".job-drag-handle"
                                                                    :group="`jobs-${team.id}`">
                                                                    <transition-group name="job-item" tag="div">
                                                                        <v-card v-for="(job, jobIndex) in team.jobs"
                                                                            :key="`job_${job.id}`"
                                                                            class="mb-2 job-item ml-4" outlined
                                                                            :class="{ 'dragging': isDragging }">
                                                                            <v-card-text class="pa-3">
                                                                                <v-row no-gutters align="center">
                                                                                    <v-col cols="auto" class="mr-3">
                                                                                        <div class="job-drag-handle">
                                                                                            <v-icon color="grey"
                                                                                                class="drag-icon"
                                                                                                x-small>
                                                                                                drag_indicator
                                                                                            </v-icon>
                                                                                        </div>
                                                                                    </v-col>
                                                                                    <v-col cols="auto" class="mr-3">
                                                                                        <div class="order-number tiny">
                                                                                            {{ jobIndex + 1 }}
                                                                                        </div>
                                                                                    </v-col>
                                                                                    <v-col>
                                                                                        <div class="job-title">{{job.name }}</div>
                                                                                        <div
                                                                                            class="job-meta text-caption grey--text">
                                                                                            <v-chip x-small
                                                                                                color="success" outlined
                                                                                                class="mr-2">
                                                                                                {{$t('status.published')}}
                                                                                            </v-chip>
                                                                                            <span
                                                                                                v-if="getLocationName(job.location)">
                                                                                                <v-icon x-small
                                                                                                    class="mr-1">location_on</v-icon>
                                                                                                {{getLocationName(job.location)}}
                                                                                            </span>
                                                                                            <span
                                                                                                v-if="getWorkTypeName(job.work_type)"
                                                                                                class="ml-2">
                                                                                                <v-icon x-small
                                                                                                    class="mr-1">business_center</v-icon>
                                                                                                {{getWorkTypeName(job.work_type)}}
                                                                                            </span>
                                                                                        </div>
                                                                                    </v-col>
                                                                                    <v-col cols="auto">
                                                                                        <v-btn icon small
                                                                                            @click="openJobPreview(job)"
                                                                                            :title="$t('tooltip.preview-job')">
                                                                                            <v-icon
                                                                                                small>visibility</v-icon>
                                                                                        </v-btn>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </v-card-text>
                                                                        </v-card>
                                                                    </transition-group>
                                                                </Draggable>
                                                            </v-expansion-panel-content>
                                                        </v-expansion-panel>
                                                    </v-expansion-panels>
                                                </transition-group>
                                            </Draggable>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </transition-group>
                        </Draggable>
                    </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" bottom>
            {{ snackbar.text }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false">
                    {{ $t('button.close') }}
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import { mapState } from 'vuex';
import Draggable from 'vuedraggable';
import axios from 'axios';

export default {
    name: 'JobSorting',
    components: {
        Draggable,
    },
    props: {
        selectedOrganizations: {
        type: Array,
            default: () => []
        }
    },
    computed: {
        ...mapState({
            userRole: state => state.currentUser.userRole,
            company: state => state.company.company,
            currentLang: state => state.currentRoute.lang,
            departments: state => state.departments.items,
            teams: state => state.teams.items,
            locations: state => state.locations.items,
            workTypes: state => state.workTypes.items,
        }),
        hasChanges() {
            return JSON.stringify(this.originalHierarchy) !== JSON.stringify(this.getCurrentHierarchy());
        },
    },
    data() {
        return {
            publishedJobs: [],
            sortedDepartments: [],
            originalHierarchy: {},
            loading: true,
            saving: false,
            isDragging: false,
            expandedDepartments: [],
            expandedTeams: [],
            snackbar: {
                show: false,
                text: '',
                color: 'success',
            },
        };
    },
    methods: {
        async loadData() {
            this.loading = true;
            try {
                // Load all required data
                await Promise.all([
                    this.$store.dispatch('departments/get', { updateStore: true, refresh: true }),
                    this.$store.dispatch('teams/get', { updateStore: true, refresh: true }),
                    this.$store.dispatch('locations/get', { updateStore: true, refresh: true }),
                    this.$store.dispatch('workTypes/get', { updateStore: true, refresh: true }),
                    this.loadJobs(),
                ]);

                this.buildSortedHierarchy();
            } catch (error) {
                this.showSnackbar('Failed to load data', 'error');
                console.error('Error loading data:', error);
            } finally {
                this.loading = false;
            }
        },

        async loadJobs() {
            const response = await this.$store.dispatch('jobs/get', {
                refresh: true,
                status: 'open',
            });

            const { items } = response.data;
            this.publishedJobs = items.filter(job =>
                job.status === 'open' && job.list_on_external === true
            );
        },

        buildSortedHierarchy() {
            const { departments, teams, publishedJobs } = this;

            const filteredDepartments = departments.filter(dept => {
                if (!this.selectedOrganizations || this.selectedOrganizations.length === 0) {
                    return true;
                }
                return this.selectedOrganizations.includes(dept.organization_id);
            });

            const sortedDepartments = filteredDepartments
                .filter(dept => {
                    const deptTeams = teams.filter(team => team.department_id === dept.id);
                    return deptTeams.some(team =>
                        publishedJobs.some(job => job.team_id === team.id)
                    );
                })
                .sort((a, b) => {
                    if (a.sort_order !== b.sort_order) {
                        return (a.sort_order || 0) - (b.sort_order || 0);
                    }
                    return a.name.localeCompare(b.name);
                })
                .map(dept => {
                    const sortedTeams = teams
                        .filter(team => team.department_id === dept.id)
                        .filter(team =>
                            publishedJobs.some(job => job.team_id === team.id)
                        )
                        .sort((a, b) => {
                            if (a.sort_order !== b.sort_order) {
                                return (a.sort_order || 0) - (b.sort_order || 0);
                            }
                            return a.name.localeCompare(b.name);
                        })
                        .map(team => {
                            const sortedJobs = publishedJobs
                                .filter(job => job.team_id === team.id)
                                .sort((a, b) => {
                                    if (a.sort_order !== b.sort_order) {
                                        return (a.sort_order || 0) - (b.sort_order || 0);
                                    }
                                    return a.name.localeCompare(b.name);
                                });

                            return {
                                ...team,
                                jobs: sortedJobs
                            };
                        });

                    return {
                        ...dept,
                        teams: sortedTeams
                    };
                });

            this.sortedDepartments = sortedDepartments;
            this.originalHierarchy = this.getCurrentHierarchy();
        },

        getCurrentHierarchy() {
            return {
                departments: this.sortedDepartments.map((dept, index) => ({
                    id: dept.id,
                    sort_order: index
                })),
                teams: this.sortedDepartments.flatMap(dept => 
                    dept.teams.map((team, index) => ({
                        id: team.id,
                        sort_order: index
                    }))
                ),
                jobs: this.sortedDepartments.flatMap(dept => 
                    dept.teams.flatMap(team => 
                        team.jobs.map((job, index) => ({
                            id: job.id,
                            sort_order: index
                        }))
                    )
                )
            };
        },

        getTotalJobsInDepartment(department) {
            return department.teams?.reduce((total, team) => total + (team.jobs?.length || 0), 0) || 0;
        },

        getLocationName(locationId) {
            if (!locationId) return '';
            const location = this.locations.find(loc => loc.id === locationId);
            return location ? location.name : '';
        },

        getWorkTypeName(workTypeId) {
            if (!workTypeId) return '';
            const workType = this.workTypes.find(wt => wt.id === workTypeId);
            return workType ? workType.name : '';
        },

        handleDragStart() {
            this.isDragging = true;
        },

        handleDragEnd() {
            this.isDragging = false;
        },

        updateExpandedDepartments(expanded) {
            this.expandedDepartments = expanded;
        },

        updateExpandedTeams(expanded) {
            this.expandedTeams = expanded;
        },

        async handleSave() {
            this.saving = true;
            try {
                const currentHierarchy = this.getCurrentHierarchy();
                
                await axios.put('/v1/hire-app/job-sort-order', {
                    departments: currentHierarchy.departments,
                    teams: currentHierarchy.teams,
                    jobs: currentHierarchy.jobs,
                    selectedOrganizations: this.selectedOrganizations
                });

                this.originalHierarchy = currentHierarchy;
                this.showSnackbar(this.$t('text.job-order-saved'), 'success');
                await this.loadData();
            } catch (error) {
                this.showSnackbar(this.$t('text.save-failed'), 'error');
                console.error('Error saving sort order:', error);
            } finally {
                this.saving = false;
            }
        },

        handleReset() {
            this.buildSortedHierarchy();
        },

        openJobPreview(job) {
            const currentLang = this.$i18n.locale || this.currentLang || 'en';
            const url = `/${currentLang}/jobs/edit/${job.id}`;
            this.$router.push(url);
        },

        showSnackbar(text, color = 'success') {
            this.snackbar = {
                show: true,
                text,
                color,
            };
        },
    },

    watch: {
        selectedOrganizations: {
            handler() {
                if (this.departments.length > 0 && this.teams.length > 0 && this.publishedJobs.length > 0) {
                    this.buildSortedHierarchy();
                }
            },
            deep: true
        }
    },

    async mounted() {
        await this.loadData();
    },
};
</script>

<style lang="scss" scoped>
.department-drag-handle,
.team-drag-handle,
.job-drag-handle {
    cursor: grab;
    padding: 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }

    &:active {
        cursor: grabbing;
    }
}

.drag-icon {
    transition: color 0.2s ease;
}

.department-drag-handle:hover .drag-icon,
.team-drag-handle:hover .drag-icon,
.job-drag-handle:hover .drag-icon {
    color: rgba(0, 0, 0, 0.6) !important;
}

.order-number {
    background-color: #f5f5f5;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 500;
    color: #666;

    &.small {
        width: 20px;
        height: 20px;
        font-size: 10px;
    }

    &.tiny {
        width: 18px;
        height: 18px;
        font-size: 9px;
    }
}

.department-title {
    font-weight: 600;
    font-size: 16px;
    line-height: 1.2;
    margin-bottom: 2px;
}

.team-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 1.2;
    margin-bottom: 2px;
}

.job-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 1.2;
    margin-bottom: 4px;
}

.department-meta,
.team-meta,
.job-meta {
    line-height: 1.3;
}

.department-item,
.team-item,
.job-item {
    transition: all 0.3s ease;

    &.dragging {
        transform: rotate(1deg);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
    }
}

.department-item-move,
.team-item-move,
.job-item-move {
    transition: transform 0.3s ease;
}

.department-item-enter-active,
.department-item-leave-active,
.team-item-enter-active,
.team-item-leave-active,
.job-item-enter-active,
.job-item-leave-active {
    transition: all 0.3s ease;
}

.department-item-enter,
.department-item-leave-to,
.team-item-enter,
.team-item-leave-to,
.job-item-enter,
.job-item-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.sortable-ghost {
    opacity: 0.5;
    background-color: #f5f5f5;
}

.sortable-chosen {
    opacity: 0.8;
}

.department-header,
.team-header {
    width: 100%;
}

// Fix expansion panel padding
::v-deep .v-expansion-panel-content__wrap {
    padding: 0 24px 16px;
}

::v-deep .v-expansion-panel-header {
    padding: 16px 24px;
}
</style>